<template>
  <div>
    <!-- Read-only visning: ikoner eller tekst -->
    <div v-if="readOnly" class="text-center">
      <template v-if="showText">
        <span v-if="modelValue === true">Sann</span>
        <span v-else-if="modelValue === false">Usann</span>
        <span v-else class="italic text-fg-muted">(blank)</span>
      </template>
      <template v-else>
        <font-awesome-icon v-if="modelValue === true" :icon="['fal', 'check']"/>
        <font-awesome-icon v-else-if="modelValue === false" :icon="['fal', 'minus']"/>
        <font-awesome-icon v-else :icon="['fal', 'question']"/>
      </template>
    </div>

    <!-- Edit mode: tri-state toggle for nullable bool -->
    <template v-else-if="header.isNullable">
      <BaseToggleGroup
        :model-value="booleanValue"
        :options="triStateOptions"
        :allow-null="true"
        :class="{ 'rounded-md border border-danger p-0.5': hasError }"
        class="compact-toggle"
        @update:model-value="changed"
      />
      <BaseProgressBar v-if="saving" :value="null" class="h-1 m-0 p-0" />
      <div v-else class="h-1 m-0 p-0">
        &nbsp;
      </div>
      <span v-if="hasError" class="text-danger">Feil ved lagring!!!</span>
    </template>

    <!-- Edit mode: switch for non-nullable bool -->
    <template v-else>
      <BaseOnOff :model-value="booleanValue" :class="{ 'rounded-md border border-danger p-0.5': hasError }" prevent-center @change="changed" />
      <BaseProgressBar v-if="saving" :value="null" class="h-1 m-0 p-0" />
      <div v-else class="h-1 m-0 p-0">
        &nbsp;
      </div>
      <span v-if="hasError" class="text-danger">Feil ved lagring!!!</span>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useTableStore } from '@/store/storeTable'
import { useCellSave } from '@/composables/useCellSave'
import BaseOnOff from '@/components/BaseComponents/BaseOnOff.vue'
import BaseToggleGroup from '@/components/BaseComponents/BaseToggleGroup.vue'
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Array, Date, Object],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  showText: {
    type: Boolean,
    default: false
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  onSaveSuccess: { type: Function, default: null },
  // US 3399: id deklareres, men videreformidles BEVISST ikke - denne komponenten rendrer ingen
  // labelable kontroll aa peke paa. Deklarasjonen finnes for aa STOPPE Vues attribute-fallthrough:
  // uten den ville en id havnet paa rot-diven, og en <label for=> dit ville tilfredsstilt statisk
  // analyse mens HTMLLabelElement.control fortsatt var null. Se editComponentAcceptsId.
  id: { type: String, default: undefined }
});

const emit = defineEmits(['update:modelValue'])


// Lagringsfeil og fremdrift eies av useCellSave; hasError styrer feilmarkering i template.
const { saving, hasSaveError: hasError, saveCell } = useCellSave()

// TableStore
const tableStore = useTableStore()

// Tri-state options for nullable bool
const triStateOptions = [
  { value: true, label: 'Ja' },
  { value: false, label: 'Nei' },
  { value: null, label: 'Ukjent' }
]

// Konverter modelValue til boolean for BaseOnOff
// BaseOnOff forventer Boolean | null, men modelValue kan være string/number fra columnDefault
const booleanValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    // For non-nullable bools, konverter null til false
    if (!props.header.isNullable) {
      return false;
    }
    return null;
  }
  if (typeof props.modelValue === 'boolean') {
    return props.modelValue;
  }
  if (typeof props.modelValue === 'string') {
    const lower = props.modelValue.toLowerCase().trim();
    if (lower === '1' || lower === 'true') return true;
    if (lower === '0' || lower === 'false' || lower === '') return false;
    return Boolean(props.modelValue);
  }
  if (typeof props.modelValue === 'number') {
    return props.modelValue !== 0;
  }
  // For andre typer (Array, Date, Object), returner null
  return null;
});

async function changed (newValue) {
  // For non-nullable bools, konverter null til false
  const finalValue = (!props.header.isNullable && (newValue === null || newValue === undefined)) ? false : newValue;
  emit('update:modelValue', finalValue)

  if (!props.autoSave) return;

  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: finalValue
  };
  await saveCell(item, props.onSaveSuccess)
}
</script>

<style scoped>
/* Kompakt styling for tri-state toggle i editBool — :deep penetrasjon
 * av BaseToggleGroup-internals beholdes som scoped CSS. Tailwind utility
 * kan ikke uttrykke :deep()-selektor. Error-staten styres nå via
 * utility-klasser direkte på wrapper (rounded-md border border-danger). */
.compact-toggle :deep(.toggle-group) {
  gap: 0.25rem;
}

.compact-toggle :deep(.toggle-option) {
  min-width: 70px;
  padding: 0.375rem 0.5rem;
  font-size: 0.85rem;
}
</style>
